import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles, Star } from "lucide-react";
import { assetUrl } from "@/lib/api";

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.85, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = ({ content }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const yContent = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const h = content.hero;
  const lmsUrl = content.brand?.lmsUrl || "https://www.lms.adhisatyacourse.online";

  return (
    <section
      id="beranda"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-20"
    >
      <motion.div style={{ y: yBg, scale: scaleBg }} className="absolute inset-0 will-change-transform">
        <img src={assetUrl(h.bgImage)} alt="Suasana belajar" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-sky-950/92 via-sky-900/78 to-sky-800/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
      </motion.div>

      <div className="absolute -top-20 -right-20 w-96 h-96 bg-sky-400/20 rounded-full blur-3xl animate-blob" />
      <div className="absolute bottom-10 left-10 w-72 h-72 bg-amber-400/10 rounded-full blur-3xl animate-blob" />

      <motion.div
        style={{ y: yContent, opacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 md:px-12 w-full py-20"
      >
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm font-medium mb-6"
          >
            <Sparkles className="w-4 h-4 text-amber-300" />
            {h.badge}
          </motion.div>

          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white leading-[1.05] tracking-tight">
            <span className="reveal-line">
              <motion.span className="block" variants={lineReveal} initial="hidden" animate="show" custom={0}>
                {h.titleLead}
              </motion.span>
            </span>
            <span className="reveal-line">
              <motion.span className="block" variants={lineReveal} initial="hidden" animate="show" custom={1}>
                <span className="text-sky-300">{h.titleHighlight1}</span>{" "}
                <span className="text-amber-300">{h.titleHighlight2}</span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-6 text-lg sm:text-xl text-sky-50/90 leading-relaxed max-w-2xl"
          >
            {h.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.62 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={lmsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-button"
              className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold shadow-xl shadow-amber-500/30 hover:-translate-y-1 transition-all"
            >
              {h.ctaText}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#tentang-kami"
              data-testid="hero-secondary-button"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white/10 backdrop-blur-md border border-white/30 text-white font-semibold hover:bg-white/20 transition-all"
            >
              Pelajari Lebih Lanjut
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-8 flex items-center gap-2 text-sky-50/90"
          >
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-amber-300 text-amber-300" />
              ))}
            </div>
            <span className="text-sm font-medium">{h.ratingText}</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          data-testid="hero-stats"
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px rounded-3xl overflow-hidden bg-white/10 backdrop-blur-xl border border-white/20"
        >
          {content.stats.map((s) => (
            <div key={s.label} className="p-6 text-center bg-white/5">
              <div className="text-3xl sm:text-4xl font-extrabold text-white">{s.value}</div>
              <div className="mt-1 text-xs sm:text-sm text-sky-100/80 font-medium">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
